import { useEffect, useRef } from 'react';
import Video from 'twilio-video';
import { ActivePane, stateType } from '../AppStateProvider';
import { getQualityScore, QualityScore } from '../../../utils/getQualityScore';
import { postMessageToParent } from '../../../postMessage/postMessageToParent';
import { QualityScoreLabel, TechCheckEventType } from '../../../postMessage/events';
import { getNetworkPathType } from '../../../utils/getNetworkPathType';

/**
 * Watches the application state and emits one-way postMessage events to the
 * embedding parent window as the user progresses through the tech check.
 *
 * Each event is guarded so that it fires at most once per occurrence, even
 * though this hook re-runs on every state change.
 *
 * The browser support, connectivity and quality checks no longer have their own
 * user-facing panes (they run together behind the RunningChecks spinner and
 * resolve to a single Results pane). Because of that, the per-category events
 * are now gated on the underlying data becoming available rather than on the
 * user reaching a particular pane, while still preserving the natural order:
 *
 *   ready -> device-check -> browser-check -> connectivity -> quality -> complete
 *
 * (the terminal `complete` and `restarted` events are emitted from the Results
 * pane, where the overall pass/fail is definitively presented to the user).
 */
export default function usePostMessageEvents(state: stateType) {
  const readySent = useRef(false);
  const deviceEventSent = useRef(false);
  const browserEventSent = useRef(false);
  const connectivityEventSent = useRef(false);
  const qualityEventSent = useRef(false);

  // App ready -> fire once on mount.
  useEffect(() => {
    if (readySent.current) return;
    readySent.current = true;
    postMessageToParent(TechCheckEventType.Ready);
  }, []);

  // Device (camera & microphone permission) result. A failure routes the user
  // to the DeviceError pane; a success advances them to the CameraTest pane.
  useEffect(() => {
    if (deviceEventSent.current) return;

    if (state.activePane === ActivePane.DeviceError && state.deviceError) {
      deviceEventSent.current = true;
      postMessageToParent(TechCheckEventType.DeviceCheckFailed, { reason: state.deviceError.message });
    } else if (state.activePane === ActivePane.CameraTest && state.audioGranted && state.videoGranted) {
      deviceEventSent.current = true;
      postMessageToParent(TechCheckEventType.DeviceCheckPassed);
    }
  }, [state.activePane, state.deviceError, state.audioGranted, state.videoGranted]);

  // Browser support result. Browser support is known synchronously, but we hold
  // the event until the user has passed the device check so the event order the
  // parent observes matches the user's journey (device -> browser).
  useEffect(() => {
    if (browserEventSent.current) return;
    if (!state.audioGranted || !state.videoGranted) return;

    browserEventSent.current = true;
    postMessageToParent(
      Video.isSupported ? TechCheckEventType.BrowserCheckPassed : TechCheckEventType.BrowserCheckFailed
    );
  }, [state.audioGranted, state.videoGranted]);

  // Connectivity result -> once the preflight test has finished (it is kicked
  // off in the background when the user leaves the GetStarted pane).
  useEffect(() => {
    if (connectivityEventSent.current) return;
    if (!state.preflightTestFinished) return;

    const connectionFailed =
      state.preflightTest.error !== null ||
      !state.preflightTest.signalingGatewayReachable ||
      !state.preflightTest.turnServersReachable;

    connectivityEventSent.current = true;

    const networkPath = getNetworkPathType(state.preflightTest.report);

    if (connectionFailed) {
      postMessageToParent(TechCheckEventType.ConnectivityFailed, {
        reason: state.preflightTest.error?.message,
        networkPath,
      });
    } else {
      postMessageToParent(TechCheckEventType.ConnectivityPassed, { networkPath });
    }
  }, [
    state.preflightTestFinished,
    state.preflightTest.error,
    state.preflightTest.signalingGatewayReachable,
    state.preflightTest.turnServersReachable,
    state.preflightTest.report,
  ]);

  // Quality & performance result -> once both the preflight and bitrate tests
  // have finished.
  useEffect(() => {
    if (qualityEventSent.current) return;
    if (!state.preflightTestFinished || !state.bitrateTestFinished) return;

    qualityEventSent.current = true;

    const { totalQualityScore, latency, jitter, packetLoss, bitrate } = getQualityScore(
      state.preflightTest.report,
      state.bitrateTest.report
    );

    const score = QualityScore[totalQualityScore] as QualityScoreLabel;
    const passed = totalQualityScore === QualityScore.Good || totalQualityScore === QualityScore.Excellent;

    postMessageToParent(TechCheckEventType.QualityResult, {
      passed,
      score,
      metrics: { latency, jitter, packetLoss, bitrate },
    });
  }, [state.preflightTestFinished, state.bitrateTestFinished, state.preflightTest.report, state.bitrateTest.report]);
}
